import { Headline, Paragraph } from '@n2-common/ui';
import { getApplicationStatus, submitSsnToGetGpid } from '@n2-common/services/legacyApplicationApiService';
import { useNavigate, useParams } from 'react-router-dom';
import style from './style.module.scss';
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import { navigateToCorrectStep } from '../App';
import { CONFIG } from '@n2-common/components/WizardFormManager/fi/helpers';
import Wizard from '@n2-common/components/MultiStageOnePageWizard';
import { useApplicationIdContext } from '@n2-common/components/ApplicationContext';
import { navigateToStep, ApplicationPaths } from '@n2-common/applicationPages/ApplicationFormsHelpers';
import { ProductTypeForTracking, trackCoreBankEvent, trackEcommerceEvent } from '@n2-common/lib/gtm';
import { SubmitFunc } from '@n2-common/components/MultiStageOnePageWizard/types';
import { useTranslation } from 'react-i18next';
import { errorToAction } from '@n2-common/applicationPages/applicationErrorHelper';
import { useLoanApplicationContext, LoanApplicationContextProvider } from './Context';
import { useWizardFormManager } from '@n2-common/components/WizardFormManager';

const productType: ProductTypeForTracking = 'CLF';

const WizardAppComponent = () => {
    const { initialValues, getWizardStages, mapForSubmit } = useWizardFormManager('FI-CL');
    const formName = 'FI_LOAN';
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { t } = useTranslation('common');
    const { applicationId, applicantId, country } = useApplicationIdContext(false, false);
    const { postLoanApplication, redirect } = useLoanApplicationContext();
    const [gpid, setGpid] = useState('');
    const { combinedLoan } = useParams();

    useEffect(() => {
        document.title = 'Hae nyt';
        trackCoreBankEvent(productType, country, 'application_start');
        trackEcommerceEvent(productType, country, 'view_item', 0);
    }, [country]);

    useEffect(() => {
        if (!(applicantId && applicationId)) {
            return;
        }

        getApplicationStatus(applicationId, applicantId).then((res) => {
            navigateToCorrectStep(res, applicantId, navigate);
        });
    }, [applicationId, navigate, applicantId]);

    const stages = useMemo(() => getWizardStages(t, CONFIG), [getWizardStages, t]);

    const onSsnStepFinished = useCallback(
        async (values: typeof initialValues) => {
            const cleanedValues = mapForSubmit(values, CONFIG);

            try {
                const [, res] = await submitSsnToGetGpid({ Ssn: cleanedValues.mainApplicant.nationalId || '' });
                startTransition(() => setGpid(res.Gpid));
                trackEcommerceEvent(productType, country, 'begin_checkout', parseInt(cleanedValues.desiredCredit, 10), res.Gpid);
            } catch (err) {
                console.error(err);
                return;
            }
        },
        [mapForSubmit, country]
    );

    const onSubmit: SubmitFunc<typeof initialValues> = useCallback(
        async (values, onProgress) => {
            if (isSubmitting) {
                return;
            }
            setIsSubmitting(true);
            const cleanedValues = mapForSubmit(values, CONFIG);
            onProgress('Submitted');
            try {
                startTransition(() => onProgress('Processing'));

                const loanAmountForTracking = parseInt(cleanedValues.desiredCredit?.replace(/\s/g, ''));
                trackCoreBankEvent(productType, country, 'application_sent', undefined, undefined, false, loanAmountForTracking, gpid);

                const result = await postLoanApplication(cleanedValues);

                redirect(result, navigate);
            } catch (err) {
                const action = errorToAction(err, formName, applicationId, applicantId, 'O2');
                if (action) {
                    onProgress({ type: 'Error', options: action });
                    setIsSubmitting(false);
                } else {
                    navigateToStep(ApplicationPaths.error, undefined, navigate, undefined);
                }
            }
        },
        [isSubmitting, mapForSubmit, country, gpid, postLoanApplication, redirect, navigate, applicationId, applicantId]
    );

    return (
        <>
            <Headline>Hae nyt</Headline>
            <Paragraph>
                Täytä tiedot ja lähetä sitoumuksetta lainahakemus. Jos haluat korottaa luottorajaasi Morrow Bankilla, ilmoitathan tällöin ainoastaan
                haluamasi korotuksen, joka ei sisällä nykyistä luottorajaasi.
            </Paragraph>
            <div className={style.form}>
                <Wizard
                    majorNumberUsedForTracking={'1'}
                    formName={formName}
                    initialValues={{
                        ...initialValues,
                        RefinanceDebt: combinedLoan !== null && combinedLoan !== undefined && combinedLoan == 'combinedLoan',
                    }}
                    onSsnStepFinished={onSsnStepFinished}
                    ssnStep={1}
                    stages={stages}
                    onSubmit={onSubmit}
                />
            </div>
        </>
    );
};

const WizardApp = () => {
    return (
        <LoanApplicationContextProvider>
            <WizardAppComponent />
        </LoanApplicationContextProvider>
    );
};

export default WizardApp;
